import { format } from 'date-fns';
import dayjs from 'dayjs';
// @ts-ignore
import { MESSAGE_STATUS } from 'shared/constants/messages';
import type { IMessageBase } from './Cards/types';
export interface ICardMessage {
  content: string;
  content_type: string;
  status: string;
  sender?: {
    id: number;
    avatar_url: string;
    thumbnail: string;
    name: string;
    type: string;
  };
  contentAttributes?: Record<string, any>;
  created_at: number;
}

export const CardType = {
  // 商品
  GOODS: 'goods',
  // 订单
  ORDER: 'order',
  // 退货
  REVERSE_ORDER: 'reverse_order',
  // 订单列表
  ORDER_LIST: 'order_list',
  // 动作
  ACTION: 'action',
  // 选择订单和商品并退货
  REFUND_INTENTION: 'refund_intention',
};

export const CardConfigs = {
  [CardType.ORDER]: {
    cardContent: 'Order',
    title: 'CONVERSATION.CARD_MESSAGE.ORDER.TITLE',
  },
  [CardType.REVERSE_ORDER]: {
    cardContent: 'ReverseOrder',
    title: 'CONVERSATION.CARD_MESSAGE.REVERSE_ORDER.TITLE',
  },
  [CardType.GOODS]: {
    cardContent: 'Goods',
  },
  [CardType.ORDER_LIST]: {
    cardContent: 'OrderList',
  },
  [CardType.ACTION]: {
    cardContent: 'Action',
  },
  [CardType.REFUND_INTENTION]: {
    cardContent: 'RefundIntention',
  },
};

interface ICardMessageContent {
  cardType: string;
  data: IMessageBase;
}

const convertTimeFormat = (placeholder: string) => {
  return placeholder
    .replace(/yyyy/g, 'YYYY') // 年，四位数
    .replace(/dd/g, 'DD') // 日 1-31
    .replace(/E/g, 'ddd'); // 英文星期几 Sun-Sat
};

/**
 * Parse a string to an object
 * @param str - The string to parse
 * @returns The parsed object or null if the string is not a valid JSON
 */
export const parseStringToObject = (
  str: string
): ICardMessageContent | null => {
  try {
    return JSON.parse(str);
  } catch (error) {
    return null;
  }
};

/**
 * 格式化订单时间
 * @param time - 时间戳
 * @returns 格式化后的时间
 */
export const formatOrderDate = (time: number) => {
  const _time = time < 10000000000 ? time * 1000 : time;
  const date = dayjs(_time);
  if (!date.isValid()) return '';
  return date.format(convertTimeFormat('dd MMM, yyyy HH:mm:ss'));
};

/**
 * 是否显示头像
 * @param cardType - 卡片类型
 * @returns 是否显示头像
 */
const SHOW_AVATAR_CARD_TYPES = [CardType.ORDER_LIST, CardType.ACTION];
const showAvatar = (cardType?: string) => {
  return !!cardType && SHOW_AVATAR_CARD_TYPES.includes(cardType);
};

/**
 * 是否显示日期
 * @param cardType - 卡片类型
 * @returns 是否显示日期
 */
const SHOW_DATE_CARD_TYPES = [CardType.ORDER_LIST];
const showDate = (cardType?: string) => {
  return !!cardType && SHOW_DATE_CARD_TYPES.includes(cardType);
};

/**
 * 是否显示内容模式
 * @param cardType - 卡片类型
 * @returns 是否显示内容模式
 */
const SHOW_CONTENT_MODE_CARD_TYPES = [CardType.ORDER_LIST];
const isContentMode = (cardType?: string) => {
  return !!cardType && SHOW_CONTENT_MODE_CARD_TYPES.includes(cardType);
};

export const getComputedProps = (message: ICardMessage, cardType?: string) => {
  const { status, sender, contentAttributes, created_at } = message;
  const isPending = status === MESSAGE_STATUS.PROGRESS;
  const isFailed = status === MESSAGE_STATUS.FAILED;
  const isSentByBot =
    !isPending && !isFailed && (!sender?.type || sender?.type === 'agent_bot');
  return {
    isPending,
    isFailed,
    isSentByBot,
    isShowAvatar: showAvatar(cardType),
    isContentMode: isContentMode(cardType),
    createdAt: contentAttributes?.external_created_at || created_at,
  };
};